import ApiService from '@/util/api.service.js'
import { getCached, setCache } from '../utils/entity-cache.js'

// setCache diferido con setTimeout(0): el write a IDB abre una transaccion
// en el mismo microtask que el callback del XHR. Cuando el XHR resolvio
// con respuesta grande (cameras 764 KB, bus 825 KB, incidents 1.6 MB),
// el RunMicrotasks queda con el write IDB encolado en serie con el
// _ingestEntities. Diferir el write con setTimeout saca el setCache del
// path critico — el operador ve los markers ~5-15 ms antes y la cache se
// persiste igual en el siguiente macrotick (no afecta correctness).
function deferredSetCache(key, items, scope) {
  setTimeout(function() { setCache(key, items, scope) }, 0)
}

// Helper de marks que no falla si bootProfiler aún no se cargó (orden de
// imports). Lee del global que el módulo ya creó al boot.
function bpMark(name) {
  try {
    if (window.__bootProfile && window.__bootProfile.mark) window.__bootProfile.mark(name)
  } catch (_) {}
}
function bpMeasure(name, startMark, endMark) {
  try {
    if (window.__bootProfile && window.__bootProfile.measure) window.__bootProfile.measure(name, startMark, endMark)
  } catch (_) {}
}
// Wrappa una promise de fetch HTTP con marks start/end. El nombre va
// tipado como `boot.fetch.<entity>` para que el reporte humano lo encuentre.
function timedFetch(entity, promise) {
  var startMark = 'boot.fetch.' + entity + '.start'
  var endMark = 'boot.fetch.' + entity + '.end'
  bpMark(startMark)
  return promise.then(function(r) {
    bpMark(endMark)
    bpMeasure('boot.fetch.' + entity, startMark, endMark)
    return r
  }, function(e) {
    bpMark(endMark)
    bpMeasure('boot.fetch.' + entity, startMark, endMark)
    throw e
  })
}

/**
 * Progressive initial data loader with IndexedDB cache.
 *
 * Flow for STATIC entities (cameras, sensors, geofences, LPR):
 *   1. Read from IndexedDB cache → show instantly (~100ms)
 *   2. Fetch from API in background
 *   3. If data changed → update map + cache
 *
 * Flow for DYNAMIC entities (devices, buses, incidents):
 *   Always fetch from API (positions change constantly)
 */
export function loadInitialDataProgressive(callbacks, options) {
  var categoryData = {}
  var groups = {}
  var opts = options || {}
  // Permisos que deciden si una fuente se pide siquiera. Ocultar en la UI no
  // basta para datos personales: /rapidsos trae el telefono del reportante y
  // sus coordenadas, asi que un operador sin permiso no debe ni disparar la
  // peticion. Default true = no cambiar el comportamiento de callers que no
  // pasan options.
  var canSeeReportants = opts.canSeeReportants !== false
  // Dueño de la caché IndexedDB: el id del usuario de la sesión. Sin él no se
  // lee ni se escribe caché (ver entity-cache.js — era compartida entre
  // usuarios del mismo navegador).
  var cacheScope = opts.cacheScope
  // Capas del mapa que el perfil puede ver (Parte D del plan de accesos por
  // perfil). Una capa prohibida no se pide ni se lee de la cache. Default
  // () => true = comportamiento previo. /geofences NUNCA pasa por aqui: es el
  // universo del filtro devices_by_geofence.
  var capaOk = typeof opts.capaPermitida === 'function' ? opts.capaPermitida : function() { return true }

  // ALL entity types are cacheable.
  // Static (cameras, sensors, LPR): positions never change, cache is always accurate.
  // Dynamic (devices, buses): positions may be stale by ~5s, but at cluster zoom
  // this is invisible, and fresh data from API replaces it within seconds.
  var CACHEABLE_TYPES = [
    'cameras', 'lpr', 'sensors', 'devices', 'positions',
    'bus', 'incidents', 'geofences',
  ]

  // Phase 0: Show ALL cached data IMMEDIATELY (before lookups even load)
  var cachePromises = []
  var cachedTypes = {} // track which types were served from cache

  // Tipo de cache -> capa del mapa (geofences no es capa gateada).
  var CAPA_DE_CACHE = {
    cameras: 'camera', lpr: 'lpr', sensors: 'sensor', devices: 'device',
    positions: 'device', bus: 'bus', incidents: 'incident',
  }
  function leerCache(cacheKey) {
    var capa = CAPA_DE_CACHE[cacheKey]
    if (capa && !capaOk(capa)) return Promise.resolve(null)
    return getCached(cacheKey, cacheScope)
  }

  CACHEABLE_TYPES.forEach(function(cacheKey) {
    cachePromises.push(
      leerCache(cacheKey).then(function(cached) {
        if (!cached || cached.length === 0) return
        cachedTypes[cacheKey] = cached.length
      })
    )
  })

  // After all cache reads, deliver cached entities
  cachePromises.push(
    Promise.all([
      leerCache('cameras'),
      leerCache('devices'),
      leerCache('positions'),
      leerCache('bus'),
      leerCache('lpr'),
      leerCache('sensors'),
      leerCache('incidents'),
      leerCache('geofences'),
    ]).then(function(results) {
      var cameras = results[0], devices = results[1], positions = results[2]
      var buses = results[3], lpr = results[4], sensors = results[5]
      var incidents = results[6], geofences = results[7]

      // Merge cached positions into cached devices
      if (devices && devices.length && positions && positions.length) {
        var posById = {}
        for (var i = 0; i < positions.length; i++) {
          var p = positions[i]
          if (p.deviceId != null) posById[p.deviceId] = p
          else if (p.id != null) posById[p.id] = p
        }
        for (var j = 0; j < devices.length; j++) {
          if (!devices[j].position && posById[devices[j].id]) {
            devices[j].position = posById[devices[j].id]
          }
        }
      }

      // Entrega SINCRONICA: invocar los 7 callbacks en el mismo task. El
      // sourceManager._scheduleDeferredFlush usa microtask con flag para
      // coalescar — si los 7 setFeatures ocurren en el mismo task, solo se
      // dispara UN setData al final y por ende UN cluster rebuild + UN
      // re-render. Todos los markers aparecen juntos.
      //
      // Una version anterior (F21 del plan de optimizacion) fragmentaba
      // con setTimeout(0) entre cada delivery para "romper el long task de
      // 664 ms". Eso introdujo regresion visual: cada setTimeout(0) ponia
      // el siguiente delivery en un task distinto, asi que cada uno disparaba
      // su propio setData -> 7 cluster rebuilds en serie. Visualmente los
      // markers aparecian por tipos (cameras primero, luego devices, etc.)
      // y el costo total subia (mas cluster rebuilds que el long task que
      // se "elimino"). Revertido a entrega sincronica por user feedback.
      if (cameras && cameras.length) { cachedTypes.cameras = cameras.length; callbacks.onEntities('camera', cameras) }
      if (devices && devices.length) { cachedTypes.devices = devices.length; callbacks.onEntities('device', devices) }
      if (buses && buses.length) { cachedTypes.bus = buses.length; callbacks.onEntities('bus', buses) }
      if (lpr && lpr.length) { cachedTypes.lpr = lpr.length; callbacks.onEntities('lpr', lpr) }
      if (sensors && sensors.length) { cachedTypes.sensors = sensors.length; callbacks.onEntities('sensor', sensors) }
      if (incidents && incidents.length) { cachedTypes.incidents = incidents.length; callbacks.onEntities('incident', incidents) }
      if (geofences && geofences.length) { cachedTypes.geofences = geofences.length; callbacks.onGeofences(geofences) }
    })
  )

  // Phase 1: Load lookups (needed for enrichment)
  bpMark('boot.lookups.start')
  var lookupsReady = Promise.all([
    ApiService.get('/v2/device-categories').catch(function() { return [] }),
    ApiService.get('/v2/incident-categories').catch(function() { return [] }),
    ApiService.get('/v2/sensor-categories').catch(function() { return [] }),
    ApiService.get('/groups').catch(function() { return [] }),
  ]).then(function(lookups) {
    bpMark('boot.lookups.end')
    bpMeasure('boot.lookups', 'boot.lookups.start', 'boot.lookups.end')
    categoryData = {
      deviceCategories: indexById(lookups[0]),
      incidentCategories: indexById(lookups[1]),
      sensorCategories: indexById(lookups[2]),
    }
    groups = indexById(lookups[3])
    return { categoryData: categoryData, groups: groups }
  })

  // Phase 2: Load all entities (API calls fire independently)
  return lookupsReady.then(function(ctx) {
    var allDone = []

    // --- Static entities: fetch API in background, update if changed ---

    // Cameras
    if (capaOk('camera')) {
      allDone.push(
        timedFetch('cameras', ApiService.get('/cameras').catch(function() { return [] })).then(function(raw) {
          var items = toArray(raw).map(function(cam) {
            cam.groupName = (ctx.groups[cam.groupId] || {}).name || ''
            return cam
          })
          // Only re-render if count changed or wasn't cached
          if (!cachedTypes.camera || cachedTypes.camera !== items.length) {
            callbacks.onEntities('camera', items)
          }
          deferredSetCache('cameras', items, cacheScope)
        })
      )
    }

    // LPR — la fuente real es `/v2/analytic-devices` (paridad con legacy
    // Map.vue:1916-1947). El endpoint `/lpr` quedó obsoleto y devuelve
    // array vacío. Cargamos también `/v2/analytic-categories` para
    // enriquecer item.categoryData como hace el legacy.
    if (capaOk('lpr')) {
      allDone.push(
        Promise.all([
          ApiService.get('/v2/analytic-categories').catch(function() { return [] }),
          ApiService.get('/v2/analytic-devices').catch(function() { return [] }),
        ]).then(function(resps) {
          var categoriesRaw = toArray(resps[0])
          var devicesRaw = toArray(resps[1])
          var categoriesById = {}
          for (var i = 0; i < categoriesRaw.length; i++) {
            if (categoriesRaw[i] && categoriesRaw[i].id != null) {
              categoriesById[categoriesRaw[i].id] = categoriesRaw[i]
            }
          }
          var items = devicesRaw.map(function(item) {
            if (item.category && categoriesById[item.category]) {
              item.categoryData = categoriesById[item.category]
            }
            if (item.groupId != null && !item.groupName) {
              item.groupName = (ctx.groups[item.groupId] || {}).name || ''
            }
            return item
          })
          // Siempre re-ingestar: el guard por conteo (cachedTypes.lpr !==
          // items.length) ocultaba ediciones de CONTENIDO — p.ej. configurar
          // cameras[] en un LPR existente no cambia el conteo y el operador
          // veía el doc cacheado sin cámaras durante todo el primer reload.
          // El dataset de LPRs es chico; el costo de re-ingestar es trivial.
          callbacks.onEntities('lpr', items)
          deferredSetCache('lpr', items, cacheScope)
        })
      )
    }

    // Sensors
    if (capaOk('sensor')) {
      allDone.push(
        timedFetch('sensor', ApiService.get('/sensor').catch(function() { return [] })).then(function(raw) {
          var items = toArray(raw).map(function(s) {
            s.groupName = (ctx.groups[s.groupId] || {}).name || ''
            if (s.category) s.category = ctx.categoryData.sensorCategories[s.category] || null
            return s
          })
          if (!cachedTypes.sensor || cachedTypes.sensor !== items.length) {
            callbacks.onEntities('sensor', items)
          }
          deferredSetCache('sensors', items, cacheScope)
        })
      )
    }

    // Geofences
    allDone.push(
      timedFetch('geofences', ApiService.get('/geofences').catch(function() { return [] })).then(function(raw) {
        var items = toArray(raw)
        // Siempre se entrega: es el universo autoritativo y MapV2 lo usa para
        // REEMPLAZAR el filtro devices_by_geofence (no sumarlo). Antes se
        // saltaba con el mismo conteo que la caché, y con dos usuarios de igual
        // número de geocercas el segundo se quedaba con las del primero.
        callbacks.onGeofences(items)
        deferredSetCache('geofences', items, cacheScope)
      })
    )

    // --- Dynamic entities: fetch fresh + cache for next load ---

    // Devices + Positions
    if (capaOk('device')) {
      allDone.push(
        Promise.all([
          timedFetch('devices', ApiService.get('/devices').catch(function() { return [] })),
          timedFetch('positions', ApiService.get('/positions').catch(function() { return [] })),
        ]).then(function(results) {
          var rawDevices = toArray(results[0])
          var rawPositions = toArray(results[1])
          var positionsById = {}
          for (var i = 0; i < rawPositions.length; i++) {
            var pos = rawPositions[i]
            if (pos.deviceId != null) positionsById[pos.deviceId] = pos
            else if (pos.id != null) positionsById[pos.id] = pos
          }
          var items = rawDevices.map(function(dev) {
            dev.groupName = (ctx.groups[dev.groupId] || {}).name || ''
            if (dev.category) dev.categoryData = ctx.categoryData.deviceCategories[dev.category] || null
            if (!dev.position && positionsById[dev.id]) dev.position = positionsById[dev.id]
            return dev
          })
          callbacks.onEntities('device', items)
          deferredSetCache('devices', items, cacheScope)
          deferredSetCache('positions', rawPositions, cacheScope)
        })
      )
    }

    // Buses
    if (capaOk('bus')) {
      allDone.push(
        timedFetch('bus', ApiService.get('/bus').catch(function() { return [] })).then(function(raw) {
          var items = toArray(raw)
          callbacks.onEntities('bus', items)
          deferredSetCache('bus', items, cacheScope)
        })
      )
    }

    // Incidents
    if (capaOk('incident')) {
      allDone.push(
        timedFetch('incidents', ApiService.get('/incidents').catch(function() { return [] })).then(function(raw) {
          var twelveHoursAgo = Date.now() - (12 * 60 * 60 * 1000)
          var items = toArray(raw)
            .filter(function(inc) {
              if (inc.status !== 'ACTIVO') return false
              // Same filter as original Map.vue: only last 12 hours
              var created = new Date(inc.createdAt || inc.serverTime).getTime()
              return created > twelveHoursAgo
            })
            .map(function(inc) {
              if (inc.type) inc.categoryData = ctx.categoryData.incidentCategories[inc.type] || null
              return inc
            })
          // Conteo CRUDO, antes de los filtros de estado/antiguedad de arriba:
          // el panel de KPIs lo usa como senal de autorizacion (una cuenta sin
          // acceso al 9-1-1 recibe cero registros del backend), y los filtros
          // del cliente son operativos, no de permisos.
          if (callbacks.onIncidentsLoaded) callbacks.onIncidentsLoaded(toArray(raw).length)
          callbacks.onEntities('incident', items)
          deferredSetCache('incidents', items, cacheScope)
        })
      )
    }

    // --- Tertiary: less critical ---

    // Recent device events (alertas tab — device alerts, geofence events)
    if (capaOk('device')) {
      allDone.push(
        ApiService.get('/events/recents').catch(function() { return [] }).then(function(raw) {
          var items = toArray(raw)
          if (items.length && callbacks.onAlerts) callbacks.onAlerts('device_events', items)
        })
      )
    }

    // SIEMPRE se pide, aun con la capa sos_call prohibida: es la fuente que
    // rehidrata la llamada activa del propio operador tras un reload. MapV2
    // (_deliverOrQueue) descarta todo salvo esa llamada.
    allDone.push(
      ApiService.get('/sosincidents?status=active').catch(function() { return [] }).then(function(raw) {
        var items = toArray(raw)
        if (items.length) callbacks.onEntities('sos_call', items)
      })
    )

    if (canSeeReportants && capaOk('reportant')) {
      allDone.push(
        ApiService.get('/rapidsos').catch(function() { return [] }).then(function(raw) {
          var items = toArray(raw)
          if (items.length) callbacks.onEntities('reportant', items)
        })
      )
    }

    // LPR / iSentry / Acuseek / mandamiento hits — NO son entities persistentes.
    // Se muestran solo en el tab "Hits" del sidebar de alertas.
    // Dos fuentes historicas:
    //   /v2/analytic/records/hits/active            → LPR hits
    //   /v2/analytic-external-records/hits/active   → iSentry, Acuseek, etc.
    // OJO con el separador: los LPR records viven bajo `analytic/records`
    // (con slash) y los externos bajo `analytic-external-records` (con guion).
    // No es simetrico — son modulos distintos del backend.
    // Ademas /v2/analytic-devices trae las camaras/dispositivos con lat/lng
    // para resolver coordenadas de cada hit (necesario para flyTo al click).
    // /murphy/lasthits queda como backup legacy (puede estar vacio en prod).
    if (capaOk('hit')) {
      allDone.push(
        Promise.all([
          ApiService.get('/v2/analytic-devices').catch(function() { return [] }),
          ApiService.get('/v2/analytic/records/hits/active').catch(function() { return [] }),
          ApiService.get('/v2/analytic-external-records/hits/active').catch(function() { return [] }),
          ApiService.get('/murphy/lasthits').catch(function() { return [] }),
        ]).then(function(results) {
          var devices = toArray(results[0])
          var lprHits = toArray(results[1])
          var extHits = toArray(results[2])
          var murphyHits = toArray(results[3])

          // Index analytic devices por id (MongoDB) para lookup al enriquecer
          var devicesById = {}
          for (var d = 0; d < devices.length; d++) {
            var dev = devices[d]
            if (dev.id) devicesById[dev.id] = dev
            if (dev._id) devicesById[dev._id] = dev
            if (dev.identifier) devicesById[dev.identifier] = dev
          }

          // Enriquecer LPR hits con analyticDevice para poder resolver coords
          var lprWithMeta = lprHits.map(function(hit) {
            if (!hit.analyticDevice && hit.analyticDeviceId) {
              hit.analyticDevice = devicesById[hit.analyticDeviceId] || null
            }
            hit.typeOfItem = 'hitLpr'
            hit.hitType = 'hitLpr'
            return hit
          })

          // Enriquecer external hits (iSentry, Acuseek) similarmente
          var extWithMeta = extHits.map(function(hit) {
            if (!hit.analyticDevice && hit.deviceId) {
              hit.analyticDevice = devicesById[hit.deviceId] || null
            }
            hit.typeOfItem = hit.model
            hit.hitType = hit.model
            return hit
          })

          var allHits = lprWithMeta.concat(extWithMeta).concat(murphyHits)
          if (allHits.length && callbacks.onHits) {
            callbacks.onHits(allHits, 'initial')
          }
        })
      )
    }

    if (capaOk('presa')) {
      allDone.push(
        ApiService.get('/v2/dam/all').catch(function() { return [] }).then(function(raw) {
          var items = toArray(raw)
          if (items.length) callbacks.onEntities('presa', items)
        })
      )
    }

    if (capaOk('sismo')) {
      allDone.push(
        ApiService.get('/v2/sismo/getRecents').catch(function() { return [] }).then(function(raw) {
          var items = toArray(raw)
          if (items.length) callbacks.onEntities('sismo', items)
        })
      )
    }

    if (capaOk('fire')) {
      allDone.push(
        ApiService.get('/v2/fire/getActives').catch(function() { return [] }).then(function(raw) {
          var items = toArray(raw)
          if (items.length) callbacks.onEntities('fire', items)
        })
      )
    }

    // Medidas cautelares: snapshot de los alertamientos del día (mismo patrón
    // que dam/sismo/fire — REST al boot + delta por WS). El backend ya aplica
    // el scoping por centro de mando del operador; aquí no se filtra nada.
    if (capaOk('medida_cautelar')) {
      allDone.push(
        ApiService.get('/v2/medidas-cautelares/alertas/hoy').catch(function() { return [] }).then(function(raw) {
          var items = toArray(raw)
          if (items.length && callbacks.onHits) callbacks.onHits(items, 'MedidaCautelarAlerta')
        })
      )
    }

    return Promise.all(allDone).then(function() {
      callbacks.onComplete(ctx.categoryData, ctx.groups)
    })
  })
}

function indexById(arr) {
  if (!Array.isArray(arr)) return {}
  var map = {}
  for (var i = 0; i < arr.length; i++) {
    var item = arr[i]
    if (item.id != null) map[item.id] = item
  }
  return map
}

function toArray(val) {
  return Array.isArray(val) ? val : []
}
